import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

/**
 * RoleGuard — a UI affordance. NOT a security boundary.
 *
 * - Always requires an authenticated session (same check as AuthGuardService).
 * - Additionally, when a route declares `data: { adminOnly: true }`, it hides the
 *   route from users without a super/client/tenant admin flag.
 *
 * SECURITY (audit obs #2 - Privilege Escalation, CWE-269), STEP 5 — "stop relying
 * on UI menu hiding as an access control".
 *
 * This guard decides what to SHOW. It cannot decide what a user may DO, and it
 * must never be cited as the control that prevents anything. It reads
 * `wto_w_user` / `wto_clients` from localStorage, which the user owns: setting
 * `spr_admn_in: 1` in devtools and reloading walks straight past `isAdmin()`.
 * Every route it guards is also reachable by typing the URL, and the underlying
 * APIs are reachable with curl and a valid token — which is precisely the audited
 * finding (a State user pasted a ULB URL and completed a privileged write).
 *
 * An earlier version of this comment claimed the guard "closes vertical privilege
 * escalation to admin-only modules". It does not, and the claim was the actual
 * hazard: it invites the next developer to add an `adminOnly` route and stop
 * there, leaving the API ungated. Corrected under obs #2 step 5.
 *
 * WHERE THE REAL CONTROL LIVES. Server-side, per route, in
 * server/api/routes/**: `checkUser.hasToken` (authentication) plus
 * `checkUser.vldSelect/vldInsert/vldUpdate/vldDelete(<object>)` (authorisation,
 * resolved from the role permissions in the database against the verified JWT).
 * For the one route this guard currently marks `adminOnly` — `/internal/clients` —
 * that control is `clientManage` on every route in
 * server/api/routes/client/clientRtr.js.
 *
 * Adding an `adminOnly` route WITHOUT a matching server-side `vld*` gate
 * re-creates the audited vulnerability. Add the server gate first; treat this
 * guard as cosmetic.
 *
 * (Compare pages/help-desk-admin/help-desk-admin.guard.ts, which documents the
 * same split correctly and is the pattern to follow.)
 *
 * Non-admin-marked routes behave exactly like the existing AuthGuard, so this is
 * non-breaking for every current route it is applied to.
 */
@Injectable({ providedIn: 'root' })
export class RoleGuard {
  constructor(private router: Router) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    let user: any = null;
    let clients: any = null;
    try { user = JSON.parse(localStorage.getItem('wto_w_user')); } catch { user = null; }
    try { clients = JSON.parse(localStorage.getItem('wto_clients')); } catch { clients = null; }

    // 1) Must be authenticated.
    if (!user || !user.usr_id) {
      this.router.navigate(['/internal']);
      return false;
    }

    // 2) If the route is admin-only, require an admin flag.
    const adminOnly = route.data && route.data['adminOnly'] === true;
    if (adminOnly && !this.isAdmin(user, clients)) {
      // Send non-admins back to their home dashboard instead of the admin page.
      this.router.navigate([localStorage.getItem('homeUrl') || '/internal/dashboard']);
      return false;
    }
    return true;
  }

  private isAdmin(user: any, clients: any): boolean {
    if (user && (user.spr_admn_in == 1 || user.clnt_admn_in == 1 || user.tnt_admn_in == 1)) {
      return true;
    }
    if (Array.isArray(clients)) {
      for (const c of clients) {
        if (c && (c.spr_admn_in == 1 || c.clnt_admn_in == 1)) { return true; }
        if (Array.isArray(c?.tnts) && c.tnts.some((t: any) => t && t.tnt_admn_in == 1)) { return true; }
      }
    }
    return false;
  }
}
